import React, { useState, useEffect, useRef } from 'react';
import { Box, Fab, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { postMeal, saveUserSettings } from '../../services/postMeal';

export interface MealPayload {
  type: 'mealPhoto' | 'barcode' | 'input';
  photo?: string;
  text?: string;
}

// ---------------------------------------------------------
// CameraDialog Component
// ---------------------------------------------------------
interface CameraDialogProps {
  open: boolean;
  onClose: () => void;
  onCapture: (data: string) => void;
  barcode?: boolean;
}

const CameraDialog: React.FC<CameraDialogProps> = ({ open, onClose, onCapture, barcode = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (open) {
      navigator.mediaDevices
        .getUserMedia({
          video: { facingMode: 'environment' },
        })
        .then((stream) => {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
        })
        .catch((err) => {
          console.error('Error accessing camera:', err);
        });
    }
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  const handleCapture = () => {
    if (!streamRef.current) return;
    captureWithCanvas();
  };

  const captureWithCanvas = () => {
    if (videoRef.current && streamRef.current) {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      const track = streamRef.current.getVideoTracks()[0];
      const settings = track.getSettings();
      canvas.width = settings.width || video.videoWidth;
      canvas.height = settings.height || video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataURL = canvas.toDataURL('image/png');
        onCapture(dataURL);
      }
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <Box position='relative' width='100%' height='100%' bgcolor='black'>
        <video
          ref={videoRef}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          autoPlay
          playsInline
          muted
        />
        {barcode && (
          <Box
            position='absolute'
            top='50%'
            left='50%'
            width='60%'
            height='30%'
            sx={{
              transform: 'translate(-50%, -50%)',
              border: '4px dashed #fff',
              borderRadius: 2,
            }}
          />
        )}
        <Box position='absolute' bottom={32} left='50%' sx={{ transform: 'translateX(-50%)' }}>
          <Fab
            onClick={handleCapture}
            sx={{
              border: '2px solid white',
              backgroundColor: 'transparent',
              color: 'white',
              width: 64,
              height: 64,
            }}
          >
            <PhotoCameraIcon fontSize='large' />
          </Fab>
        </Box>
      </Box>
    </Dialog>
  );
};

// ---------------------------------------------------------
// ManualInputDialog Component
// ---------------------------------------------------------
interface ManualInputDialogProps {
  open: boolean;
  title: string;
  placeholder: string;
  onClose: () => void;
  onSubmit: (text: string) => void;
}

const ManualInputDialog: React.FC<ManualInputDialogProps> = ({ title, placeholder, open, onClose, onSubmit }) => {
  const [input, setInput] = useState('');
  const handleSubmit = () => {
    onSubmit(input);
    setInput('');
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin='dense'
          label={placeholder}
          fullWidth
          variant='outlined'
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant='contained'>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------
// UserInputDialog Component
// ---------------------------------------------------------
interface UserInputDialogProps {
  open: boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (data: {
    userEmail: string;
    currentWeight: number;
    height: number;
    goalWeight: number;
    calorieDailyGoal: number;
  }) => void;
}

const UserInputDialog: React.FC<UserInputDialogProps> = ({ open, onClose, onSubmit, isPending }) => {
  const [data, setData] = useState<{
    userEmail: string;
    currentWeight: number | '';
    height: number | '';
    goalWeight: number | '';
    calorieDailyGoal: number | '';
  }>({
    userEmail: '',
    currentWeight: '',
    height: '',
    goalWeight: '',
    calorieDailyGoal: '',
  });

  const handleSubmit = () => {
    if (
      data.userEmail.trim() &&
      data.currentWeight !== '' &&
      data.height !== '' &&
      data.goalWeight !== '' &&
      data.calorieDailyGoal !== ''
    ) {
      onSubmit({
        userEmail: data.userEmail.trim(),
        currentWeight: Number(data.currentWeight),
        height: Number(data.height),
        goalWeight: Number(data.goalWeight),
        calorieDailyGoal: Number(data.calorieDailyGoal),
      });
      setData({
        userEmail: '',
        currentWeight: '',
        height: '',
        goalWeight: '',
        calorieDailyGoal: '',
      });
    } else {
      alert('Please fill out all fields.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Information required</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          type='email'
          margin='dense'
          label='Your email'
          fullWidth
          variant='outlined'
          value={data.userEmail}
          onChange={(e) =>
            setData({
              ...data,
              userEmail: e.target.value,
            })
          }
        />
        <TextField
          type='number'
          margin='dense'
          label='Your height'
          fullWidth
          variant='outlined'
          value={data.height}
          onChange={(e) =>
            setData({
              ...data,
              height: Number(e.target.value),
            })
          }
        />
        <TextField
          type='number'
          margin='dense'
          label='Your weight'
          fullWidth
          variant='outlined'
          value={data.currentWeight}
          onChange={(e) =>
            setData({
              ...data,
              currentWeight: Number(e.target.value),
            })
          }
        />
        <TextField
          type='number'
          margin='dense'
          label='Your goal weight'
          fullWidth
          variant='outlined'
          value={data.goalWeight}
          onChange={(e) =>
            setData({
              ...data,
              goalWeight: Number(e.target.value),
            })
          }
        />
        <TextField
          type='number'
          margin='dense'
          label='Your daily calorie intake'
          fullWidth
          variant='outlined'
          value={data.calorieDailyGoal}
          onChange={(e) =>
            setData({
              ...data,
              calorieDailyGoal: Number(e.target.value),
            })
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant='contained' disabled={isPending}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------
// Main Component: AddFoodComponent
// ---------------------------------------------------------
interface AddFoodComponentProps {
  mutation: UseMutationResult<any, Error, { payload: MealPayload; userEmail: string }, unknown>;
}

const AddFoodComponent: React.FC<AddFoodComponentProps> = ({ mutation }) => {
  const [openOptions, setOpenOptions] = useState(false);
  const [openScanFood, setOpenScanFood] = useState(false);
  const [openScanBarcode, setOpenScanBarcode] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const [openUserEmail, setOpenUserEmail] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const queryClient = useQueryClient();

  const settingsMutation = useMutation({
    mutationFn: saveUserSettings,
    onSuccess: async () => {
      setOpenUserEmail(false);
      setUserEmail(localStorage.getItem('userEmail') || '');
      setOpenOptions(true);
      await queryClient.invalidateQueries({ queryKey: ['track'] });
      await queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const handleScanFood = () => {
    setOpenOptions(false);
    setOpenScanFood(true);
  };

  const handleScanBarcode = () => {
    setOpenOptions(false);
    setOpenScanBarcode(true);
  };

  const handleAddManually = () => {
    setOpenOptions(false);
    setOpenManual(true);
  };

  const saveUserEmail = (data: any) => {
    localStorage.setItem('userEmail', data.userEmail);
    setUserEmail(data.userEmail);
    settingsMutation.mutate(data);
  };

  const getUserEmail = () => {
    return localStorage.getItem('userEmail');
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem('userEmail');
    if (!storedEmail) {
      setOpenUserEmail(true);
    } else {
      setUserEmail(storedEmail);
    }
  }, []);

  return (
    <>
      <Fab
        color='primary'
        sx={{ width: 80, height: 80, position: 'fixed', bottom: '16px', right: '16px' }}
        onClick={() => {
          if (getUserEmail()) {
            setOpenOptions(true);
          } else {
            setOpenUserEmail(true);
          }
        }}
      >
        <AddIcon fontSize='large' />
      </Fab>

      <Dialog open={openOptions} onClose={() => setOpenOptions(false)}>
        <DialogTitle>Select an Option</DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2} mt={1}>
            <Button variant='contained' onClick={handleScanFood}>
              Scan Food
            </Button>
            <Button variant='contained' onClick={handleScanBarcode} disabled>
              Scan Bar Code
            </Button>
            <Button variant='contained' onClick={handleAddManually} disabled>
              Add Manually
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <CameraDialog
        open={openScanFood}
        onClose={() => setOpenScanFood(false)}
        onCapture={(data) => {
          mutation.mutate({ payload: { type: 'mealPhoto', photo: data }, userEmail });
          setOpenScanFood(false);
        }}
      />

      <CameraDialog
        open={openScanBarcode}
        onClose={() => setOpenScanBarcode(false)}
        onCapture={(data) => {
          mutation.mutate({ payload: { type: 'barcode', photo: data }, userEmail });
          setOpenScanBarcode(false);
        }}
        barcode
      />

      <ManualInputDialog
        open={openManual}
        title='Add manual'
        placeholder='Food details'
        onClose={() => setOpenManual(false)}
        onSubmit={(text: string) => {
          mutation.mutate({ payload: { type: 'input', text }, userEmail });
          setOpenManual(false);
        }}
      />

      <UserInputDialog
        open={openUserEmail}
        isPending={settingsMutation.isPending}
        onClose={() => setOpenUserEmail(false)}
        onSubmit={(data: any) => {
          saveUserEmail(data);
        }}
      />
    </>
  );
};

export default AddFoodComponent;
